import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402/api';

// Integration Types
export enum IntegrationType {
  DISCORD = 'discord',
  SLACK = 'slack',
  TWITTER = 'twitter',
  YOUTUBE = 'youtube',
  WEBHOOK = 'webhook',
  ZAPIER = 'zapier',
}

// Integration Action Types
export enum IntegrationAction {
  POST_PUBLISHED = 'post_published',
  NEW_PATRON = 'new_patron',
  MEMBERSHIP_CANCELLED = 'membership_cancelled',
  PAYMENT_RECEIVED = 'payment_received',
  MILESTONE_REACHED = 'milestone_reached',
  MESSAGE_SENT = 'message_sent',
  SYNC_COMPLETED = 'sync_completed',
}

// TypeScript Interfaces
export interface Integration {
  id: string;
  userId: string;
  name: string;
  type: IntegrationType;
  config: Record<string, any>;
  isActive: boolean;
  isConnected: boolean;
  lastSyncAt?: string;
  lastError?: string;
  hasCredentials: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    logs: number;
  };
}

export interface IntegrationLog {
  id: string;
  integrationId: string;
  action: string;
  status: 'success' | 'error' | 'pending';
  requestData?: any;
  responseData?: any;
  errorMessage?: string;
  duration?: number;
  createdAt: string;
}

export interface IntegrationType_Definition {
  type: IntegrationType;
  name: string;
  description: string;
  icon: string;
  comingSoon?: boolean;
  fields?: IntegrationField[];
}

export interface IntegrationField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
}

export interface CreateIntegrationInput {
  name: string;
  type: IntegrationType;
  config: Record<string, any>;
  credentials?: Record<string, any>;
}

export interface UpdateIntegrationInput {
  name?: string;
  config?: Record<string, any>;
  credentials?: Record<string, any>;
  isActive?: boolean;
}

export interface TriggerActionInput {
  action: IntegrationAction;
  payload: Record<string, any>;
}

// API Client
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// ==================== Integration CRUD ====================

export const getAvailableTypes = async (): Promise<IntegrationType_Definition[]> => {
  const response = await axios.get(`${API_BASE_URL}/integrations/types`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getIntegrations = async (): Promise<Integration[]> => {
  const response = await axios.get(`${API_BASE_URL}/integrations`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getIntegration = async (id: string): Promise<Integration> => {
  const response = await axios.get(`${API_BASE_URL}/integrations/${id}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const createIntegration = async (data: CreateIntegrationInput): Promise<Integration> => {
  const response = await axios.post(`${API_BASE_URL}/integrations`, data, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const updateIntegration = async (
  id: string,
  data: UpdateIntegrationInput
): Promise<Integration> => {
  const response = await axios.put(`${API_BASE_URL}/integrations/${id}`, data, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const deleteIntegration = async (id: string): Promise<{ success: boolean }> => {
  const response = await axios.delete(`${API_BASE_URL}/integrations/${id}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// ==================== Integration Actions ====================

export const testIntegration = async (id: string): Promise<{ success: boolean; message: string }> => {
  const response = await axios.post(`${API_BASE_URL}/integrations/${id}/test`, {}, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const triggerIntegration = async (
  id: string,
  data: TriggerActionInput
): Promise<any> => {
  const response = await axios.post(`${API_BASE_URL}/integrations/${id}/trigger`, data, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getIntegrationLogs = async (
  id: string,
  limit = 50,
  offset = 0
): Promise<{ logs: IntegrationLog[]; total: number }> => {
  const response = await axios.get(`${API_BASE_URL}/integrations/${id}/logs`, {
    params: { limit, offset },
    headers: getAuthHeaders(),
  });
  return response.data;
};

// ==================== Utility Functions ====================

export const getIntegrationIcon = (type: IntegrationType): string => {
  switch (type) {
    case IntegrationType.DISCORD:
      return '🎮';
    case IntegrationType.SLACK:
      return '💬';
    case IntegrationType.TWITTER:
      return '🐦';
    case IntegrationType.YOUTUBE:
      return '▶️';
    case IntegrationType.WEBHOOK:
      return '🔗';
    case IntegrationType.ZAPIER:
      return '⚡';
    default:
      return '🔌';
  }
};

export const getIntegrationStatusColor = (integration: Integration): string => {
  if (!integration.isActive) {
    return 'text-gray-500 bg-gray-100';
  }
  if (integration.isConnected && !integration.lastError) {
    return 'text-green-700 bg-green-100';
  }
  if (integration.lastError) {
    return 'text-red-700 bg-red-100';
  }
  return 'text-yellow-700 bg-yellow-100';
};

export const getIntegrationStatusText = (integration: Integration): string => {
  if (!integration.isActive) {
    return 'Inactive';
  }
  if (integration.isConnected && !integration.lastError) {
    return 'Connected';
  }
  if (integration.lastError) {
    return 'Error';
  }
  return 'Pending';
};

export const getLogStatusColor = (status: string): string => {
  switch (status) {
    case 'success':
      return 'text-green-700 bg-green-100';
    case 'error':
      return 'text-red-700 bg-red-100';
    case 'pending':
      return 'text-yellow-700 bg-yellow-100';
    default:
      return 'text-gray-700 bg-gray-100';
  }
};

export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)}w ago`;
  return date.toLocaleDateString();
};

export default {
  getAvailableTypes,
  getIntegrations,
  getIntegration,
  createIntegration,
  updateIntegration,
  deleteIntegration,
  testIntegration,
  triggerIntegration,
  getIntegrationLogs,
  getIntegrationIcon,
  getIntegrationStatusColor,
  getIntegrationStatusText,
  getLogStatusColor,
  formatRelativeTime,
  IntegrationType,
  IntegrationAction,
};
